import { useMemo } from 'react';
import { css } from '@emotion/react';
import { formatDate } from '@frontend/date';
import { useTranslation } from '@frontend/i18n';
import { Icon } from '@frontend/icons';
import { formatPhoneNumber } from '@frontend/phone-numbers';
import { ScheduleRequestListItem } from '@frontend/schedule-helpers';
import { theme } from '@frontend/theme';
import { Button, Text, useModalControl } from '@frontend/design-system';
import { useCheckCanSendMessages } from '../../hooks/use-check-can-send-messages';
import type { ScheduleRequestActionCalledFrom } from '../../hooks/use-schedule-request-actions';
import { ScheduleRequestsTrackingIds } from '../../trackingIds';

interface ScheduleRequestDetailsProps {
  listItem: ScheduleRequestListItem;
  patientContactedAt?: string;
  dismissRequestModalProps: ReturnType<typeof useModalControl>;
  onDismissRequestIntent: (actionCalledFrom?: ScheduleRequestActionCalledFrom) => void;
  onMessage: (updateStatus: boolean, actionCalledFrom?: ScheduleRequestActionCalledFrom) => void;
  onPhoneCall: (actionCalledFrom?: ScheduleRequestActionCalledFrom) => void;
  onCompleteRequest: (actionCalledFrom?: ScheduleRequestActionCalledFrom) => void;
  onReviewCallLog: () => void;
  onViewConversation: () => void;
}

export const PreferenceBasedScheduleRequestDetails = ({
  listItem,
  patientContactedAt,
  dismissRequestModalProps,
  onDismissRequestIntent,
  onMessage,
  onPhoneCall,
  onCompleteRequest,
  onReviewCallLog,
  onViewConversation,
}: ScheduleRequestDetailsProps) => {
  const { t } = useTranslation('schedule-request');
  const canSendMessages = useCheckCanSendMessages(listItem.locationId ?? '');
  const isPatientContacted = !!patientContactedAt;
  const lastContactedOn = isPatientContacted ? formatDate(patientContactedAt, 'MMM D, YYYY, h:mm A') : null;

  const aiRequestDetails = listItem.aiRequestDetails;
  const bookingCaptureSource = aiRequestDetails?.sourceDetails?.source;
  const phoneNumber = listItem.schedulee?.phoneNumber;
  const email = listItem.schedulee?.email;
  const birthDate = listItem.schedulee?.birthDate;
  const availabilityPreference = aiRequestDetails?.schedulingPreferences?.requestedSlots?.[0]?.rawText;
  const reasonForVisit = aiRequestDetails?.visitDetails?.reasonForVisit;
  const channelId = aiRequestDetails?.sourceDetails?.channelId;

  const hasValidConversationLog = useMemo(() => {
    if (bookingCaptureSource !== 'VOICE_AI' && bookingCaptureSource !== 'SMS_AI') return false;
    if (bookingCaptureSource === 'VOICE_AI') return !!channelId;
    return true;
  }, [bookingCaptureSource, channelId]);

  return (
    <div css={containerStyles}>
      <div>
        <Text size='small' color='subdued'>
          {t('Contact information')}
        </Text>
        <span css={contactItemStyles}>
          <Icon name='phone-small' />
          <Text size='small' color='light'>
            {formatPhoneNumber(phoneNumber)}
          </Text>
        </span>
        {email && (
          <span css={contactItemStyles}>
            <Icon name='email-small' />
            <Text size='small' color='light'>
              {email}
            </Text>
          </span>
        )}
      </div>

      {birthDate && (
        <div>
          <Text size='small' color='subdued'>
            {t('Date of Birth')}
          </Text>
          <Text size='small' color='light'>
            {formatDate(birthDate, 'MMMM D, YYYY')}
          </Text>
        </div>
      )}

      {availabilityPreference && (
        <div>
          <Text size='small' color='subdued'>
            {t('Availability Preferences')}
          </Text>
          <Text size='small' color='light'>
            {availabilityPreference}
          </Text>
        </div>
      )}

      {reasonForVisit && (
        <div>
          <Text size='small' color='subdued'>
            {t('Reason for Visit')}
          </Text>
          <Text size='small' color='light'>
            {reasonForVisit}
          </Text>
        </div>
      )}

      {hasValidConversationLog && (
        <div css={viewThreadButtonStyles}>
          {bookingCaptureSource === 'VOICE_AI' && (
            <Button
              variant='tertiary'
              iconName='phone'
              trackingId={ScheduleRequestsTrackingIds.requestListItemReviewCallLogBtn}
              onClick={onReviewCallLog}
            >
              {t('Review Call Log')}
            </Button>
          )}
          {bookingCaptureSource === 'SMS_AI' && (
            <Button
              variant='tertiary'
              iconName='message'
              trackingId={ScheduleRequestsTrackingIds.requestListItemViewConversationBtn}
              onClick={onViewConversation}
            >
              {t('View Conversation')}
            </Button>
          )}
        </div>
      )}

      <div css={footerContainerStyles}>
        {isPatientContacted && (
          <div>
            <Text size='small' color='subdued'>
              {t('Contacted on {{lastContactedOn}}', { lastContactedOn })}
            </Text>
          </div>
        )}
        <div css={footerButtonStyles}>
          <div css={contactButtonStyles}>
            <Button
              iconName='message'
              variant='secondary'
              disabled={!canSendMessages}
              hoverLabel={canSendMessages ? undefined : t('You do not have permission to send text messages')}
              onClick={() => onMessage(true, 'request_details')}
            >
              {t('Message')}
            </Button>
            <Button iconName='phone' variant='secondary' onClick={() => onPhoneCall('request_details')}>
              {t('Call')}
            </Button>
          </div>
          {isPatientContacted ? (
            <Button variant='tertiary' onClick={() => onCompleteRequest('request_details')}>
              {t('Done')}
            </Button>
          ) : (
            <Button
              variant='tertiary'
              onClick={() => {
                onDismissRequestIntent('request_details');
                dismissRequestModalProps?.openModal();
              }}
            >
              {t('Dismiss')}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

const containerStyles = css({
  display: 'flex',
  flexDirection: 'column',
  gap: 'var(--fabric-space-gap-md)',
  padding: 'var(--fabric-space-padding-sm)',
  borderRadius: 'var(--fabric-radius-sm)',
  boxShadow: theme.shadows.light,
  background: theme.colors.white,
});

const contactItemStyles = css`
  display: flex;
  align-items: center;
  gap: var(--fabric-space-gap-xxs);
  color: var(--fabric-color-neutral-70);
`;

const viewThreadButtonStyles = css`
  & > button {
    padding: var(--fabric-space-padding-sm) var(--fabric-space-padding-xxs);
  }
`;

const footerContainerStyles = css({
  display: 'flex',
  flexDirection: 'column',
  gap: 'var(--fabric-space-gap-xxs)',
});

const footerButtonStyles = css`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--fabric-space-gap-xl);
`;

const contactButtonStyles = css`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--fabric-space-gap-sm);
  flex: 1;

  & > button {
    color: var(--fabric-color-neutral-70);
    flex: 1;
  }
`;
